export interface CSVFieldDefinition {
  key: string;
  label: string;
  required: boolean;
}

export const RETIREMENT_401K_CSV_FIELDS: CSVFieldDefinition[] = [
  { key: 'fundName', label: 'Fund Name', required: true },
  { key: 'ticker', label: 'Ticker', required: false },
  { key: 'shares', label: 'Shares', required: true },
  { key: 'balance', label: 'Balance / Market Value', required: true },
  { key: 'employer', label: 'Employer', required: false },
  { key: 'accountType', label: 'Account Type', required: false },
  { key: 'costBasis', label: 'Cost Basis', required: false },
];

export const INVESTMENT_CSV_FIELDS: CSVFieldDefinition[] = [
  { key: 'symbol', label: 'Symbol / Ticker', required: true },
  { key: 'shares', label: 'Shares / Quantity', required: true },
  { key: 'costBasis', label: 'Cost Basis / Avg Cost', required: true },
  { key: 'account', label: 'Account Type', required: false },
];

/**
 * Parse CSV text into rows of string arrays.
 * Handles quoted fields, commas inside quotes, and CRLF/LF line endings.
 */
export const parseCSV = (text: string): string[][] => {
  const rows: string[][] = [];
  let current = '';
  let inQuotes = false;
  let row: string[] = [];

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    const next = text[i + 1];

    if (inQuotes) {
      if (ch === '"' && next === '"') {
        current += '"';
        i++; // skip escaped quote
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        current += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === ',') {
        row.push(current.trim());
        current = '';
      } else if (ch === '\r' && next === '\n') {
        row.push(current.trim());
        current = '';
        if (row.some(cell => cell !== '')) rows.push(row);
        row = [];
        i++; // skip \n
      } else if (ch === '\n') {
        row.push(current.trim());
        current = '';
        if (row.some(cell => cell !== '')) rows.push(row);
        row = [];
      } else {
        current += ch;
      }
    }
  }

  // Handle last field
  row.push(current.trim());
  if (row.some(cell => cell !== '')) rows.push(row);

  return rows;
};

// Mapping of common CSV header patterns to our field keys
const RETIREMENT_HEADER_PATTERNS: Record<string, string[]> = {
  fundName: ['fund name', 'fund', 'name', 'description', 'investment name', 'holding', 'security', 'security name', 'investment'],
  ticker: ['ticker', 'symbol', 'ticker symbol', 'fund symbol'],
  shares: ['shares', 'units', 'quantity', 'share balance', 'total shares'],
  balance: ['balance', 'market value', 'current value', 'value', 'total value', 'account value', 'total market value', 'current balance', 'amount'],
  employer: ['employer', 'company', 'plan', 'plan name', 'employer name'],
  accountType: ['account type', 'type', 'plan type', 'account'],
  costBasis: ['cost basis', 'cost', 'total cost', 'contributions', 'total contributions', 'employee contributions'],
};

const INVESTMENT_HEADER_PATTERNS: Record<string, string[]> = {
  symbol: ['symbol', 'ticker', 'ticker symbol', 'stock symbol', 'security symbol', 'security'],
  shares: ['shares', 'quantity', 'qty', 'qty (quantity)', 'units', 'share balance', 'total shares'],
  costBasis: ['cost basis', 'cost', 'avg cost', 'average cost', 'cost per share', 'price paid', 'purchase price', 'cost basis per share', 'cost basis total'],
  account: ['account', 'account type', 'account name', 'portfolio'],
};

/**
 * Auto-map CSV column headers to field keys using fuzzy matching.
 * Returns a mapping of field key -> column index.
 */
const autoMapWithPatterns = (headers: string[], patterns: Record<string, string[]>): Record<string, number> => {
  const mapping: Record<string, number> = {};
  const normalizedHeaders = headers.map(h => h.toLowerCase().trim());

  for (const [fieldKey, fieldPatterns] of Object.entries(patterns)) {
    for (const pattern of fieldPatterns) {
      const idx = normalizedHeaders.findIndex(h => h === pattern);
      if (idx !== -1 && !Object.values(mapping).includes(idx)) {
        mapping[fieldKey] = idx;
        break;
      }
    }
    // If exact match failed, try partial match
    if (mapping[fieldKey] === undefined) {
      for (const pattern of fieldPatterns) {
        const idx = normalizedHeaders.findIndex(h => h.includes(pattern) || pattern.includes(h));
        if (idx !== -1 && !Object.values(mapping).includes(idx)) {
          mapping[fieldKey] = idx;
          break;
        }
      }
    }
  }

  return mapping;
};

export const autoMapColumns = (headers: string[]): Record<string, number> =>
  autoMapWithPatterns(headers, RETIREMENT_HEADER_PATTERNS);

export const autoMapInvestmentColumns = (headers: string[]): Record<string, number> =>
  autoMapWithPatterns(headers, INVESTMENT_HEADER_PATTERNS);

/**
 * Find the actual header row index by scanning for rows that match known column patterns.
 * Handles brokerage CSVs (e.g. Schwab) that have metadata rows before the real headers.
 */
const ALL_KNOWN_HEADERS = new Set([
  ...Object.values(RETIREMENT_HEADER_PATTERNS).flat(),
  ...Object.values(INVESTMENT_HEADER_PATTERNS).flat(),
]);

export const findHeaderRow = (rows: string[][]): number => {
  for (let i = 0; i < Math.min(rows.length, 10); i++) {
    const normalized = rows[i].map(h => h.toLowerCase().trim());
    const matches = normalized.filter(h => ALL_KNOWN_HEADERS.has(h));
    if (matches.length >= 2) return i;
  }
  return 0;
};

/**
 * Strip currency symbols and commas from a string and parse as number.
 */
const parseNumeric = (value: string): number => {
  const cleaned = value.replace(/[$,%]/g, '').replace(/,/g, '').trim();
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
};

export interface Mapped401kRow {
  fundName: string;
  ticker: string;
  shares: number;
  balance: number;
  employer: string;
  accountType: string;
  costBasis: number;
}

export interface MappedInvestmentRow {
  symbol: string;
  shares: number;
  costBasis: number;
  account: string;
}

/**
 * Apply a column mapping to CSV data rows, producing typed 401k holdings.
 */
export const applyMapping = (
  rows: string[][],
  mapping: Record<string, number>,
  defaults: Record<string, string> = {}
): Mapped401kRow[] => {
  return rows
    .map(row => {
      const get = (key: string, fallback: string = '') => {
        const idx = mapping[key];
        if (idx !== undefined && idx < row.length) return row[idx];
        return defaults[key] || fallback;
      };

      const fundName = get('fundName');
      if (!fundName) return null;

      return {
        fundName,
        ticker: get('ticker'),
        shares: parseNumeric(get('shares', '0')),
        balance: parseNumeric(get('balance', '0')),
        employer: get('employer'),
        accountType: get('accountType', '401k Traditional'),
        costBasis: parseNumeric(get('costBasis', '0')),
      };
    })
    .filter((row): row is Mapped401kRow => row !== null && row.balance > 0);
};

/**
 * Apply a column mapping to CSV data rows, producing typed investment rows.
 */
/**
 * Detect and split a multi-account CSV (e.g. Schwab "All Accounts" export).
 * Returns sections with account labels and their data rows.
 * Returns null if the CSV is not multi-account.
 *
 * Format: account label row → header row → data rows → separator rows → next section
 */
export interface CSVSection {
  accountLabel: string;
  headerRow: string[];
  dataRows: string[][];
}

const SCHWAB_ACCOUNT_PATTERNS: Record<string, string> = {
  'roth': 'Roth IRA',
  'simple': 'SIMPLE IRA',
  'traditional': 'Traditional IRA',
  'individual': 'Individual',
  'primary': 'Individual',
  'money_market': 'Money Market',
  'money market': 'Money Market',
};

function guessAccountType(label: string): string {
  const lower = label.toLowerCase();
  for (const [pattern, accountType] of Object.entries(SCHWAB_ACCOUNT_PATTERNS)) {
    if (lower.includes(pattern)) return accountType;
  }
  // Use the raw label (cleaned up) as a custom account name
  return label.replace(/[._]/g, ' ').replace(/\s*\.\.\.\d+$/, '').trim() || 'Individual';
}

function isHeaderRow(row: string[]): boolean {
  const normalized = row.map(h => h.toLowerCase().trim());
  const matches = normalized.filter(h => ALL_KNOWN_HEADERS.has(h));
  return matches.length >= 2;
}

function isSeparatorRow(row: string[]): boolean {
  const first = (row[0] || '').toLowerCase().trim();
  return (
    row.every(cell => !cell.trim()) ||
    first.startsWith('cash & cash invest') ||
    first === 'account total' ||
    first.startsWith('positions for')
  );
}

export const detectMultiAccountCSV = (rows: string[][]): CSVSection[] | null => {
  const sections: CSVSection[] = [];
  let i = 0;

  while (i < rows.length) {
    // Skip empty/separator rows
    if (isSeparatorRow(rows[i])) { i++; continue; }

    // Look for pattern: label row followed by header row
    if (i + 1 < rows.length && !isHeaderRow(rows[i]) && isHeaderRow(rows[i + 1])) {
      const accountLabel = (rows[i][0] || '').trim();
      const headerRow = rows[i + 1];
      i += 2;

      // Collect data rows until next separator/section
      const dataRows: string[][] = [];
      while (i < rows.length && !isSeparatorRow(rows[i]) && !isHeaderRow(rows[i])) {
        // Also stop if next row looks like a section label (non-header, followed by header)
        if (i + 1 < rows.length && !isHeaderRow(rows[i]) && isHeaderRow(rows[i + 1]) && rows[i][0]?.trim() && !rows[i][1]?.trim()) {
          break;
        }
        dataRows.push(rows[i]);
        i++;
      }

      if (dataRows.length > 0) {
        sections.push({
          accountLabel: guessAccountType(accountLabel),
          headerRow,
          dataRows,
        });
      }
    } else {
      i++;
    }
  }

  // Only treat as multi-account if we found 2+ sections
  return sections.length >= 2 ? sections : null;
};

export const applyInvestmentMapping = (
  rows: string[][],
  mapping: Record<string, number>,
  defaultAccount?: string,
): MappedInvestmentRow[] => {
  const accountFallback = defaultAccount || 'Individual';
  return rows
    .map(row => {
      const get = (key: string, fallback: string = '') => {
        const idx = mapping[key];
        if (idx !== undefined && idx < row.length) return row[idx];
        return fallback;
      };

      const symbol = get('symbol').toUpperCase().trim();
      if (!symbol) return null;

      const shares = parseNumeric(get('shares', '0'));
      const costBasis = parseNumeric(get('costBasis', '0'));
      const account = get('account', accountFallback) || accountFallback;

      if (shares <= 0) return null;

      return { symbol, shares, costBasis, account };
    })
    .filter((row): row is MappedInvestmentRow => row !== null);
};
